import React, { useState, useEffect } from 'react';
import { Col, Row, Spin } from 'antd';
import { FileImageOutlined, UploadOutlined } from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';
import Dragger from 'antd/es/upload/Dragger';
import styled from 'styled-components';
import { API_URL } from '../../../../helpers/constants';
import { ITemplateItem } from '..';

interface TemplateImageUploadProps {
  value?: string;
  onChange?: (value: string) => void;
  options: ITemplateItem[];
}

const TemplateImageUpload: React.FC<TemplateImageUploadProps> = ({ value, onChange, options }) => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [loading, setLoading] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    return () => {
      fileList.forEach((file) => {
        if (file.preview && typeof file.preview === 'string' && file.preview.startsWith('blob:')) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [fileList]);

  const beforeUpload = (file: File) => {
    const isJPG = file.type === 'image/jpeg' || file.type === 'image/jpg';
    const isPNG = file.type === 'image/png';

    if (!isJPG && !isPNG) {
      return false;
    }

    return true;
  };

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: true,
    accept: '.jpg, .jpeg, .png',
    action: `${API_URL}/api/v1/admin/notifications/upload`,
    showUploadList: false,
    headers: { Authorization: `Bearer ${token}` },
    fileList: fileList,
    beforeUpload: beforeUpload,
    onChange({ file, fileList: newFileList }) {
      const convertNewFileList = newFileList.map((item) => {
        if (!item.preview && !item.response?.data && item.originFileObj) {
          const preview = URL.createObjectURL(item.originFileObj);
          return {
            ...item,
            preview: preview,
            url: preview,
          };
        }
        if (item.status === 'done' && item.response?.data) {
          if (item.preview && typeof item.preview === 'string' && item.preview.startsWith('blob:')) {
            URL.revokeObjectURL(item.preview);
          }
          return {
            ...item,
            url: item.response.data,
          };
        }
        return {
          ...item,
          url: item.response?.data || item.preview || item.url,
        };
      });
      setFileList(convertNewFileList);

      // Update loading state based on upload status
      const isUploading = newFileList.some((item) => item.status === 'uploading');
      setLoading(isUploading);

      if (file.status === 'done' && file.response && file.response.data) {
        onChange?.(file.response.data);
      }
    },
  };

  const handleClickImage = (urlImage: string) => {
    onChange?.(urlImage);
  };

  return (
    <Container>
      <Spin spinning={loading}>
        {value ? (
          <ImageContainer>
            <img src={value} alt='template preview' />
          </ImageContainer>
        ) : (
          <EmptyTemplateImageStyled>
            <FileImageOutlined style={{ fontSize: '45px' }} />
          </EmptyTemplateImageStyled>
        )}
      </Spin>

      <Row gutter={[10, 10]}>
        <Col span={8}>
          <DraggerStyled {...uploadProps}>
            <UploadOutlined style={{ fontSize: '30px' }} />
          </DraggerStyled>
        </Col>
        {options?.map((item) => {
          const isSelected = item.urlImage === value;
          return (
            <MiniColStyled
              key={item.id}
              className={isSelected ? 'active' : ''}
              span={8}
              onClick={() => onChange?.(item.urlImage)}
            >
              <MiniImageStyled
                src={item.urlImage}
                alt={`template ${item.id}`}
                style={{ cursor: 'pointer' }}
                width={'100%'}
              />
            </MiniColStyled>
          );
        })}
        {fileList?.map((file) => {
          const previewUrl = file.url || file.preview;
          if (!previewUrl) return null;

          const isSelected = file.response?.data === value || previewUrl === value;
          return (
            <MiniColStyled
              key={file.uid || file.name}
              className={isSelected ? 'active' : ''}
              span={8}
              onClick={() => handleClickImage(previewUrl)}
            >
              <MiniImageStyled src={previewUrl} alt={file.name} style={{ cursor: 'pointer' }} width={'100%'} />
            </MiniColStyled>
          );
        })}
      </Row>
    </Container>
  );
};

export default TemplateImageUpload;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const DraggerStyled = styled(Dragger)`
  .ant-upload {
    width: 100%;
    height: 100px;
  }
`;

const MiniImageStyled = styled.img`
  width: 100%;
  height: 100px;
  object-fit: contain;
`;

const MiniColStyled = styled(Col)`
  &.active {
    border-color: #1890ff;
  }
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8e8e8;
  border-radius: 5px;
  background: #f5f5f5;
  cursor: pointer;
  transition: all 0.3s;
  &:hover {
    opacity: 0.8;
    border-color: red;
  }
`;

const EmptyTemplateImageStyled = styled.div`
  width: 100%;
  height: 300px;
  background: #f5f5f5;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 300px;
  background: #f5f5f5;
  border-radius: 5px;
  border: 1px solid #e8e8e8;
  padding: 16px;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;